import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, PlayCircle, LifeBuoy, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { ARTICLES, HELP_SECTIONS } from './articles';
import { ArticleBody } from './ArticleBody';
import { findTourById } from './tours';
import { useTour } from './TourProvider';
import type { HelpArticle } from './types';

function matches(a: HelpArticle, q: string): boolean {
  if (!q) return true;
  const hay = [
    a.title, a.summary, ...(a.keywords ?? []),
    ...a.body.flatMap((b) =>
      b.type === 'p' || b.type === 'tip' || b.type === 'warn' || b.type === 'heading'
        ? [b.text]
        : b.type === 'steps' || b.type === 'list'
          ? b.items
          : b.type === 'terms'
            ? b.items.flatMap((t) => [t.term, t.def])
            : [],
    ),
  ].join(' ').toLowerCase();
  return hay.includes(q.toLowerCase());
}

export function HelpCenterPage() {
  const navigate = useNavigate();
  const { startTour } = useTour();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(ARTICLES[0].id);

  const filtered = useMemo(
    () => ARTICLES.filter((a) => matches(a, query)),
    [query],
  );

  // se a busca esconder o artigo selecionado, seleciona o primeiro resultado
  const selected =
    filtered.find((a) => a.id === selectedId) ?? filtered[0] ?? null;

  const tour = findTourById(selected?.tourId);

  const handleStartTour = () => {
    if (!tour) return;
    navigate(tour.route);
    // espera a tela do tour renderizar antes de destacar os elementos
    setTimeout(() => startTour(tour.id), 700);
  };

  return (
    <div className="mx-auto max-w-6xl p-6 lg:p-8">
      {/* cabeçalho */}
      <div className="mb-6 flex items-start gap-4">
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl gradient-brand text-white">
          <LifeBuoy className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Central de Ajuda</h1>
          <p className="text-sm text-muted-foreground">
            Guia completo do MyShow, tela por tela. Procure um tópico ou navegue pelas seções.
          </p>
        </div>
      </div>

      {/* busca */}
      <div className="relative mb-6 max-w-xl">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar na ajuda (ex.: parear TV, promoção, fatura)…"
          className="h-11 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        {/* índice */}
        <nav className="space-y-5 lg:max-h-[calc(100vh-220px)] lg:overflow-y-auto lg:pr-1">
          {HELP_SECTIONS.map((section) => {
            const items = filtered.filter((a) => a.section === section);
            if (items.length === 0) return null;
            return (
              <div key={section}>
                <div className="mb-1.5 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {section}
                </div>
                <div className="space-y-0.5">
                  {items.map((a) => {
                    const ItemIcon = a.icon;
                    const active = selected?.id === a.id;
                    return (
                      <button
                        key={a.id}
                        onClick={() => setSelectedId(a.id)}
                        className={cn(
                          'flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors',
                          active ? 'bg-brand-100 font-medium text-brand-800' : 'text-foreground hover:bg-muted',
                        )}
                      >
                        <ItemIcon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{a.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p className="px-2 text-sm text-muted-foreground">
              Nada encontrado para “{query}”.
            </p>
          )}
        </nav>

        {/* artigo */}
        <article className="min-w-0">
          {selected ? (
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-brand-100 text-brand-700">
                    <selected.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">{selected.title}</h2>
                    <p className="text-sm text-muted-foreground">{selected.summary}</p>
                  </div>
                </div>
                {tour && (
                  <Button variant="outline" size="sm" onClick={handleStartTour} className="shrink-0">
                    <PlayCircle className="h-4 w-4" /> Ver tour
                  </Button>
                )}
              </div>

              <ArticleBody blocks={selected.body} />

              {selected.route && (
                <div className="mt-6 border-t border-border pt-4">
                  <Button variant="ghost" size="sm" onClick={() => navigate(selected.route!)}>
                    Ir para esta tela →
                  </Button>
                </div>
              )}
            </div>
          ) : null}

          {/* fale com o suporte */}
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-muted/40 p-4 text-sm">
            <MessageCircle className="h-5 w-5 text-emerald-600" />
            <span className="text-muted-foreground">Não encontrou o que precisava?</span>
            <a
              href="https://wa.me/5546991136456"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-brand-600 hover:underline"
            >
              Falar com o suporte no WhatsApp
            </a>
          </div>
        </article>
      </div>
    </div>
  );
}
